/** Instrument detail: image gallery, specs, and the buy-request form. */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import BuyRequestWizard from '../components/BuyRequestWizard'
import Lightbox from '../components/Lightbox'
import { FramedImage, Row, Section, SectionHeading, StatusBadge } from '../components/ui'
import { formatPrice, mediaUrl, type Product } from '../lib/api'
import { useApi, useDocumentMeta, useLocale } from '../lib/hooks'
import { pathFor, t } from '../lib/i18n'

export default function ProductDetail() {
  const locale = useLocale()
  const strings = t(locale)
  const { slug } = useParams<{ slug: string }>()
  const { data: product, loading, error } = useApi<Product>(
    slug ? `/api/products/${encodeURIComponent(slug)}?locale=${locale}` : null,
  )
  const [activeImage, setActiveImage] = useState(0)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // A different instrument means starting from its first photo again.
  useEffect(() => {
    setActiveImage(0)
    setWizardOpen(false)
    setLightboxOpen(false)
  }, [slug])

  useDocumentMeta(
    product ? `${product.name} | zvidori` : 'zvidori',
    product?.description?.slice(0, 160),
  )

  if (loading) {
    return (
      <Section>
        <Row>
          <p className="font-sans text-[18px] text-slate">{strings.loading}</p>
        </Row>
      </Section>
    )
  }

  if (error || !product) {
    return (
      <Section>
        <Row className="text-center">
          <SectionHeading as="h1">{strings.shop.notFound}</SectionHeading>
          <Link
            to={pathFor(locale, 'shop')}
            className="mt-4 inline-block font-sans text-[16px] font-semibold text-ink underline underline-offset-4"
          >
            {strings.shop.backToShop}
          </Link>
        </Row>
      </Section>
    )
  }

  const images = product.images
  const current = images[activeImage] ?? images[0]

  return (
    <>
      <Section className="!pb-6">
        <Row>
          <Link
            to={pathFor(locale, 'shop')}
            className="font-sans text-[15px] font-semibold text-slate transition-colors hover:text-ink"
            data-testid="back-to-shop"
          >
            ← {strings.shop.backToShop}
          </Link>
        </Row>
      </Section>

      <Section className="!pt-6">
        <Row>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              {current ? (
                // Every photo shows in the same 3:4 frame; tapping opens the
                // fullscreen viewer with the highest resolution.
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={strings.gallery.imageCounter
                    .replace('{i}', String(activeImage + 1))
                    .replace('{n}', String(images.length))}
                  data-testid="product-image-open"
                  className="block w-full cursor-zoom-in text-left"
                >
                  <FramedImage
                    key={current.id}
                    filename={current.filename}
                    alt={current.alt || product.name}
                    loading="eager"
                    sizes="(max-width: 1024px) 100vw, 520px"
                    aspect="aspect-[3/4]"
                  />
                </button>
              ) : null}

              {images.length > 1 ? (
                <div className="mt-4 flex flex-wrap gap-3" data-testid="product-thumbs">
                  {images.map((image, index) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      aria-label={image.alt || `${product.name} ${index + 1}`}
                      aria-current={index === activeImage ? 'true' : undefined}
                      data-testid="product-thumb"
                      className={`h-[72px] w-[72px] overflow-hidden rounded-[2px] border-2 transition-colors ${
                        index === activeImage ? 'border-ink' : 'border-line hover:border-slate'
                      }`}
                    >
                      <img
                        src={mediaUrl(image.filename, 480)}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div>
              <h1 className="m-0 font-serif text-[clamp(34px,4.5vw,48px)] font-medium leading-[1.1] text-ink">
                {product.name}
              </h1>
              {product.subtitle ? (
                <p className="m-0 mt-2 font-serif text-[24px] italic leading-[1.35] text-slate">
                  {product.subtitle}
                </p>
              ) : null}

              <div className="mt-5 flex flex-wrap items-center gap-4">
                <span
                  className="font-sans text-[20px] font-semibold text-ink"
                  data-testid="product-price"
                >
                  {formatPrice(product, locale)}
                </span>
                <StatusBadge status={product.status} label={strings.shop[product.status]} />
              </div>

              {product.status !== 'sold' ? (
                <div className="mt-7">
                  <button
                    type="button"
                    onClick={() => setWizardOpen(true)}
                    data-testid="buy-request-open"
                    className="inline-flex cursor-pointer items-center rounded-[2px] bg-ink px-8 py-[15px] font-sans text-[15px] font-semibold leading-none text-paper transition-colors hover:bg-walnut"
                  >
                    {strings.shop.requestToBuy}
                  </button>
                  <p className="m-0 mt-3 max-w-[420px] font-sans text-[15px] leading-[1.5] text-slate">
                    {strings.shop.intro}
                  </p>
                </div>
              ) : null}

              <dl className="mt-8 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 border-t border-line pt-6 font-sans text-[17px] text-ink">
                {product.year ? (
                  <>
                    <dt className="text-slate">{strings.shop.year}</dt>
                    <dd>{product.year}</dd>
                  </>
                ) : null}
                {product.model ? (
                  <>
                    <dt className="text-slate">{strings.shop.model}</dt>
                    <dd>{product.model}</dd>
                  </>
                ) : null}
                {product.category_name ? (
                  <>
                    <dt className="text-slate">{strings.shop.category}</dt>
                    <dd>
                      <Link
                        to={`${pathFor(locale, 'shop')}?${locale === 'de' ? 'kategorie' : 'category'}=${product.category_slug}`}
                        className="underline"
                      >
                        {product.category_name}
                      </Link>
                    </dd>
                  </>
                ) : null}
              </dl>

              {product.description ? (
                <div className="mt-6 font-sans text-[18px] leading-[1.65] text-ink">
                  {product.description.split('\n').map((line, index) => (
                    <p key={index} className="mb-[1em]">
                      {line}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </Row>
      </Section>

      {wizardOpen ? (
        <BuyRequestWizard product={product} onClose={() => setWizardOpen(false)} />
      ) : null}

      {lightboxOpen ? (
        <Lightbox
          items={images.map((image) => ({
            filename: image.filename,
            alt: image.alt || product.name,
            title: product.name,
          }))}
          index={activeImage}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setActiveImage}
        />
      ) : null}
    </>
  )
}
